import React from 'react';
import type { AIFormState } from '@trinity/chat';
import type { apiSchemas } from '@trinity/management-app-client';
import type {
  GroupBase,
  MultiValue,
  SingleValue,
  ActionMeta,
  MultiValueProps,
  SingleValueProps,
  StylesConfig,
  CSSObjectWithLabel,
} from 'react-select';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';
import CreatableSelect from 'react-select/creatable';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useFormFileUpload } from '@trinity/chat-components/hooks/useFormFileUpload';
import { CustomDatePicker } from '@trinity/chat-components/components/Chat/DatePicker';
import { buildSelectStyles, buildSelectTheme } from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { useTheme } from '@trinity/chat-components/components/Theme';
import {
  FieldLabel,
  Input,
  FormRowFullWidth,
  FormColumn,
  Textarea,
  THEME,
  getThemeValue,
  getThemeColor,
} from './DynamicForm.styles.index';

const AI_ANIMATION = {
  DURATION: 1500,
  PHASES: {
    IDLE: 'idle' as const,
    PENDING: 'pending' as const,
    ANIMATING: 'animating' as const,
    APPLIED: 'applied' as const,
  },
} as const;

type AnimationPhase = typeof AI_ANIMATION.PHASES[keyof typeof AI_ANIMATION.PHASES];

const CONSTANTS = {
  MAX_INSIGHT_LENGTH: 10000,
  MIN_INSIGHT_LENGTH: 10,
  MESSAGES: {
    NOT_SPECIFIED: 'Not specified',
    NONE_SELECTED: 'None selected',
  },
} as const;

type CreatableSelectOption = { id: string; name: string };
type FormFieldValue = string | string[] | null | undefined;
type FormField = apiSchemas.FieldDefinition;

type CustomSelectStyles = Partial<StylesConfig<CreatableSelectOption, boolean, GroupBase<CreatableSelectOption>>>;

interface CreatableSelectProps {
  options: CreatableSelectOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  isMulti?: boolean;
  placeholder?: string;
  invalidValues?: string[];
  className?: string;
  style?: React.CSSProperties;
}

export const CreatableSelectField: React.FC<CreatableSelectProps> = ({
  options,
  value,
  onChange,
  isMulti = false,
  placeholder,
  invalidValues = [],
  className,
  style,
}) => {
  const theme = useTheme();
  const selectStyles = React.useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = React.useMemo(() => buildSelectTheme(theme), [theme]);

  const optionMap = React.useMemo(() => {
    const map: Record<string, CreatableSelectOption> = {};
    options.forEach(opt => { map[opt.id] = opt; });
    return map;
  }, [options]);

  const getOption = (val: string): CreatableSelectOption => {
    return optionMap[val] || { id: val, name: val };
  };

  const selectValue = React.useMemo(() => {
    if (isMulti) {
      return Array.isArray(value) ? value.map(getOption) : [];
    } else {
      return typeof value === 'string' && value ? getOption(value) : null;
    }
  }, [value, isMulti, optionMap]);

  const customStyles = React.useMemo((): CustomSelectStyles => ({
    control: (base: CSSObjectWithLabel) => {
      const controlStyles: CSSObjectWithLabel = {
        ...base,
      };
      if (className && className.includes('ai-pulse-outline')) {
        controlStyles.animation = 'pulseOutline 1.5s ease-in-out 1';
      }
      if (className && className.includes('needs-correction')) {
        controlStyles.borderColor = '#ff4444';
      }
      return controlStyles;
    },
    ...selectStyles,
    multiValue: (base: CSSObjectWithLabel, props: MultiValueProps<CreatableSelectOption, true, GroupBase<CreatableSelectOption>>) => {
      const isInvalid = invalidValues.includes(props.data.id);
      return {
        ...base,
        backgroundColor: isInvalid ? '#ffeaea' : base.backgroundColor,
      };
    },
    multiValueLabel: (base: CSSObjectWithLabel, props: MultiValueProps<CreatableSelectOption, true, GroupBase<CreatableSelectOption>>) => {
      const isInvalid = invalidValues.includes(props.data.id);
      return {
        ...base,
        color: isInvalid ? '#d32f2f' : base.color,
      };
    },
    multiValueRemove: (base: CSSObjectWithLabel, props: MultiValueProps<CreatableSelectOption, true, GroupBase<CreatableSelectOption>>) => {
      const isInvalid = invalidValues.includes(props.data.id);
      const hoverStyle = base[':hover'] as CSSObjectWithLabel | undefined;
      return {
        ...base,
        color: isInvalid ? '#d32f2f' : base.color,
        ':hover': {
          backgroundColor: isInvalid ? '#ffcdd2' : hoverStyle?.backgroundColor,
          color: isInvalid ? '#b71c1c' : hoverStyle?.color,
        },
      };
    },
    singleValue: (base: CSSObjectWithLabel, props: SingleValueProps<CreatableSelectOption, false, GroupBase<CreatableSelectOption>>) => {
      const isInvalid = invalidValues.includes(props.data.id);
      return {
        ...base,
        fontSize: '14px',
        color: isInvalid ? '#d32f2f' : base.color,
      };
    },
  }), [selectStyles, invalidValues, className]);

  const handleCreateOption = (input: string) => {
    if (isMulti) {
      if (Array.isArray(value)) {
        onChange([...value, input]);
      } else {
        onChange([input]);
      }
    } else {
      onChange(input);
    }
  };

  const handleChange = React.useCallback((val: MultiValue<CreatableSelectOption> | SingleValue<CreatableSelectOption>, _meta: ActionMeta<CreatableSelectOption>) => {
    if (isMulti) {
      if (Array.isArray(val)) {
        onChange(val.map(v => v.id));
      } else {
        onChange([]);
      }
    } else {
      if (val && typeof val === 'object' && !Array.isArray(val) && 'id' in val) {
        onChange(val.id);
      } else {
        onChange('');
      }
    }
  }, [onChange, isMulti]);

  return (
    <div className={className} style={style}>
      <CreatableSelect
        isMulti={isMulti}
        options={options}
        value={selectValue}
        onChange={handleChange}
        onCreateOption={handleCreateOption}
        placeholder={placeholder}
        styles={customStyles}
        theme={selectTheme}
        getOptionLabel={o => o.name}
        getOptionValue={o => o.id}
        menuPlacement="auto"
        isClearable
        classNamePrefix="react-select"
        formatCreateLabel={inputValue => inputValue ? `Add "${inputValue}"` : ''}
      />
    </div>
  );
};

// Helper to create field label with AI indicators
const createFieldLabel = (
  field: FormField,
  fieldId: string,
  shouldShowAsCorrected: boolean,
  shouldShowAsNeedsReview: boolean,
  isAnimating: boolean,
): React.ReactNode => {
  const isRequired = field.required;

  return (
    <FieldLabel htmlFor={fieldId}>
      {field.label}
      {isRequired && <span className="required-asterisk">*</span>}
      {shouldShowAsCorrected && (
        <span
          className="ai-pulse"
          style={{
            fontSize: THEME.fontSize.xs,
            color: '#4caf50',
            fontWeight: 'normal',
            marginLeft: '4px',
          }}
        >
          Corrected
        </span>
      )}
      {shouldShowAsNeedsReview && (
        <span
          style={{
            fontSize: THEME.fontSize.xs,
            color: '#ff4444',
            fontWeight: 'normal',
            marginLeft: '4px',
          }}
        >
          Needs review
        </span>
      )}
      {isAnimating && (
        <span
          style={{
            fontSize: THEME.fontSize.xs,
            color: '#2196f3',
            fontWeight: 'normal',
            marginLeft: '4px',
          }}
        >
          Correcting...
        </span>
      )}
    </FieldLabel>
  );
};

// Helper to create common field styles
const createFieldStyles = (
  isReadOnly: boolean,
  fieldError: string | undefined,
  shouldShowAsNeedsReview: boolean,
) => {
  const readOnlyStyle = isReadOnly ? {
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
    color: '#666',
  } : {};

  const readOnlyDivStyle = isReadOnly ? {
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    color: '#666',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
  } : {};

  const errorStyle = fieldError ? {
    borderColor: getThemeColor.error(undefined),
    borderWidth: '2px',
  } : {};

  const needsCorrectionStyle = shouldShowAsNeedsReview ? {
    borderColor: '#ff4444',
    borderWidth: '2px',
  } : {};

  return { readOnlyStyle, readOnlyDivStyle, errorStyle, needsCorrectionStyle };
};

// Helper to create error message element
const createErrorMessage = (fieldError: string | undefined): React.ReactNode => {
  if (!fieldError) return null;

  return (
    <div
      role="alert"
      style={{
        fontSize: '12px',
        color: getThemeColor.error(undefined),
        marginTop: '4px',
      }}
    >
      {fieldError}
    </div>
  );
};

// Render text field
const renderTextField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;
  const { readOnlyStyle, errorStyle, needsCorrectionStyle } = styles;

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <Input
        id={fieldId}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder || ''}
        readOnly={isReadOnly}
        className={aiCorrectionClassName}
        style={{ ...readOnlyStyle, ...errorStyle, ...needsCorrectionStyle }}
        title={field.helpText}
      />
      {errorMessage}
    </FieldWrapper>
  );
};

// Render date field
const renderDateField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  needsCorrectionClassName: string,
  fieldError: string | undefined,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;
  const isRequired = field.required;
  const { readOnlyStyle, errorStyle, needsCorrectionStyle } = styles;

  if (isReadOnly) {
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <Input
          id={fieldId}
          type="date"
          value={value || ''}
          readOnly
          disabled
          className={aiCorrectionClassName}
          style={{ ...readOnlyStyle, ...errorStyle, ...needsCorrectionStyle }}
        />
        {errorMessage}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <div className={`${aiCorrectionClassName} ${needsCorrectionClassName}`} style={needsCorrectionStyle}>
        <CustomDatePicker
          id={fieldId}
          name={field.name}
          value={(value as string) || ''}
          onChange={(date) => onChange(field.name, date)}
          placeholder={field.placeholder || 'Select date...'}
          required={isRequired}
          error={fieldError}
        />
      </div>
      {errorMessage}
    </FieldWrapper>
  );
};

// Render datetime field
const renderDateTimeField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;
  const { readOnlyStyle, errorStyle, needsCorrectionStyle } = styles;

  if (isReadOnly) {
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <Input
          id={fieldId}
          type="datetime-local"
          value={value || ''}
          readOnly
          disabled
          className={aiCorrectionClassName}
          style={{ ...readOnlyStyle, ...errorStyle, ...needsCorrectionStyle }}
        />
        {errorMessage}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <Input
        id={fieldId}
        type="datetime-local"
        value={(value as string) || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder || ''}
        readOnly={isReadOnly}
        className={aiCorrectionClassName}
        style={{ ...readOnlyStyle, ...errorStyle, ...needsCorrectionStyle }}
        title={field.helpText}
      />
      {errorMessage}
    </FieldWrapper>
  );
};

// Render select field
const renderSelectField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  needsCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const { readOnlyDivStyle, errorStyle, needsCorrectionStyle } = styles;

  if (isReadOnly) {
    const selectedOption = field.options?.find(opt => opt.value === value);
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <div
          className={aiCorrectionClassName}
          style={{ ...readOnlyDivStyle, ...errorStyle, ...needsCorrectionStyle }}
        >
          {selectedOption?.label || value || CONSTANTS.MESSAGES.NOT_SPECIFIED}
        </div>
        {errorMessage}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <CreatableSelectField
        options={field.options?.map(opt => ({ id: opt.value, name: opt.label })) || []}
        value={typeof value === 'string' ? value : ''}
        onChange={val => onChange(field.name, val)}
        isMulti={false}
        placeholder={field.placeholder || 'Select an option...'}
        invalidValues={
          value && typeof value === 'string' && !field.options?.some(opt => opt.value === value)
            ? [value]
            : []
        }
        className={`${aiCorrectionClassName} ${needsCorrectionClassName}`}
        style={needsCorrectionStyle}
      />
      {errorMessage}
    </FieldWrapper>
  );
};

// Render multiselect field
const renderMultiSelectField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  needsCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const { readOnlyDivStyle, errorStyle, needsCorrectionStyle } = styles;

  if (isReadOnly) {
    const selectedLabels = Array.isArray(value)
      ? value.map(val => field.options?.find(opt => opt.value === val)?.label || val).join(', ')
      : CONSTANTS.MESSAGES.NONE_SELECTED;
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <div
          className={aiCorrectionClassName}
          style={{ ...readOnlyDivStyle, ...errorStyle, ...needsCorrectionStyle }}
        >
          {selectedLabels}
        </div>
        {errorMessage}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <CreatableSelectField
        options={field.options?.map(opt => ({ id: opt.value, name: opt.label })) || []}
        value={Array.isArray(value) ? value : []}
        onChange={val => onChange(field.name, val)}
        isMulti={true}
        placeholder={field.placeholder || 'Select options...'}
        invalidValues={
          Array.isArray(value)
            ? value.filter(val => !field.options?.some(opt => opt.value === val))
            : []
        }
        className={`${aiCorrectionClassName} ${needsCorrectionClassName}`}
        style={needsCorrectionStyle}
      />
      {errorMessage}
    </FieldWrapper>
  );
};

// Render radio field
const renderRadioField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  needsCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
  FieldWrapper: typeof FormColumn | typeof FormRowFullWidth,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;
  const isRequired = field.required;
  const { readOnlyDivStyle, errorStyle, needsCorrectionStyle } = styles;

  if (!field.options || field.options.length === 0) {
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <div style={{ color: getThemeColor.textSecondary(undefined), fontSize: THEME.fontSize.sm }}>
          No options available
        </div>
        {errorMessage}
      </FieldWrapper>
    );
  }

  if (isReadOnly) {
    const selectedOption = field.options.find(opt => opt.value === value);
    return (
      <FieldWrapper key={field.id}>
        {fieldLabel}
        <div
          className={aiCorrectionClassName}
          style={{ ...readOnlyDivStyle, ...errorStyle, ...needsCorrectionStyle }}
        >
          {selectedOption?.label || value || CONSTANTS.MESSAGES.NOT_SPECIFIED}
        </div>
        {errorMessage}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={field.id}>
      {fieldLabel}
      <div
        className={`${aiCorrectionClassName} ${needsCorrectionClassName}`}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: THEME.spacing.md,
          ...needsCorrectionStyle,
        }}
      >
        {field.options.map((option) => (
          <label
            key={option.id}
            htmlFor={`${fieldId}-${option.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: THEME.spacing.sm,
              cursor: 'pointer',
              fontSize: THEME.fontSize.sm,
              color: getThemeColor.text(undefined),
              flex: '0 0 calc(33.333% - 12px)',
              minWidth: '120px',
              maxWidth: 'calc(33.333% - 12px)',
            }}
          >
            <input
              type="radio"
              id={`${fieldId}-${option.id}`}
              name={field.name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(field.name, e.target.value)}
              required={isRequired}
              className="radio-input"
              style={{
                cursor: 'pointer',
                width: '18px',
                height: '18px',
                flexShrink: 0,
                margin: 0,
                padding: 0,
                accentColor: getThemeColor.primary(undefined),
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {errorMessage}
    </FieldWrapper>
  );
};

// Render textarea field
const renderTextAreaField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  aiCorrectionClassName: string,
  styles: ReturnType<typeof createFieldStyles>,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;
  const isRequired = field.required;
  const { readOnlyStyle, errorStyle, needsCorrectionStyle } = styles;

  if (isReadOnly) {
    return (
      <FormRowFullWidth key={field.id}>
        {fieldLabel}
        <Textarea
          id={fieldId}
          name={field.name}
          value={value || ''}
          readOnly
          disabled
          minRows={3}
          className={aiCorrectionClassName}
          style={{
            ...readOnlyStyle,
            ...errorStyle,
            ...needsCorrectionStyle,
            resize: 'none',
          }}
        />
        {errorMessage}
      </FormRowFullWidth>
    );
  }

  return (
    <FormRowFullWidth key={field.id}>
      {fieldLabel}
      <Textarea
        id={fieldId}
        name={field.name}
        value={value || ''}
        onChange={(e) => {
          const newValue = e.target.value;
          if (newValue.length <= CONSTANTS.MAX_INSIGHT_LENGTH) {
            onChange(field.name, newValue);
          }
        }}
        placeholder={field.placeholder || ''}
        minRows={3}
        required={isRequired}
        title={field.helpText}
        className={aiCorrectionClassName}
        style={{ ...errorStyle, ...needsCorrectionStyle }}
      />
      {errorMessage}
      {field.name === 'insight' && value && value.length < CONSTANTS.MIN_INSIGHT_LENGTH && (
        <div
          role="alert"
          style={{
            fontSize: '12px',
            color: '#ffc107',
            marginTop: '4px',
          }}
        >
          Please provide at least {CONSTANTS.MIN_INSIGHT_LENGTH} characters for the insight.
        </div>
      )}
    </FormRowFullWidth>
  );
};

// ---------------------------------------------------------------------------
// File upload field components
// ---------------------------------------------------------------------------

/**
 * Displays a previously-uploaded file in read-only (submitted) view.
 * Fetches a short-lived presigned download URL and shows a thumbnail for
 * images or a file-type icon + name for all other files.
 */
const FileFieldReadOnly: React.FC<{
  identifier: string;
  fieldId: string;
  fieldLabel: React.ReactNode;
  errorMessage: React.ReactNode;
}> = ({ identifier, fieldId, fieldLabel, errorMessage }) => {
  const apiClient = useChatApiClient();
  const theme = useTheme();

  const { data, isLoading } = useQuery({
    queryKey: ['form-file-download-url', identifier],
    queryFn: () => apiClient.formFileUpload.getDownloadUrl(identifier),
    staleTime: 4 * 60 * 1000, // 4 min — URL expires in 5 min
    enabled: !!identifier,
  });

  const isImage = data?.fileType.startsWith('image/') ?? false;

  return (
    <FormRowFullWidth key={fieldId}>
      {fieldLabel}
      {isLoading && (
        <div style={{ padding: '8px 0', color: '#888', fontSize: '13px' }}>Loading file…</div>
      )}
      {!isLoading && data && (
        <a
          href={data.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            backgroundColor: '#fafafa',
            color: '#333',
            textDecoration: 'none',
            fontSize: '13px',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {isImage ? (
            <ImageIcon style={{ fontSize: '18px', color: theme.palette.btnPrimary.color, flexShrink: 0 }} />
          ) : (
            <DescriptionIcon style={{ fontSize: '18px', color: theme.palette.btnPrimary.color, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.fileName}
          </span>
          <OpenInNewIcon style={{ fontSize: '14px', color: '#888', flexShrink: 0 }} />
        </a>
      )}
      {!isLoading && !data && identifier && (
        <div style={{ padding: '8px 0', color: '#888', fontSize: '13px' }}>File not available</div>
      )}
      {errorMessage}
    </FormRowFullWidth>
  );
};

/**
 * Active (editable) file upload input. Uses useFormFileUpload to orchestrate
 * the queue → STS-creds → S3 → complete flow, then calls onChange with the
 * returned identifier string.
 */
const FileFieldUpload: React.FC<{
  field: FormField;
  value: FormFieldValue;
  onChange: (fieldName: string, newValue: FormFieldValue) => void;
  fieldLabel: React.ReactNode;
  errorMessage: React.ReactNode;
  fieldError: string | undefined;
}> = ({ field, value, onChange, fieldLabel, errorMessage, fieldError }) => {
  const { state, upload, reset } = useFormFileUpload();
  const theme = useTheme();
  const fieldId = `dynamic-field-${field.id}`;
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const identifier = await upload(file);
    if (identifier) {
      onChange(field.name, identifier);
    }

    // Reset the input value so the same file can be re-selected after a failure
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isUploading = state.status === 'queuing' || state.status === 'uploading' || state.status === 'completing';
  const hasFile = state.status === 'success' || (typeof value === 'string' && value.length > 0);
  const displayFileName = state.fileName ?? (typeof value === 'string' && value ? value : null);

  const borderColor = fieldError ? '#e53935' : '#ccc';

  return (
    <FormRowFullWidth key={field.id}>
      {fieldLabel}

      <div
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          padding: '12px 14px',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Upload trigger row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label
            htmlFor={fieldId}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: `1px solid ${isUploading ? theme.palette.btnPrimary.disabled.border : theme.palette.btnPrimary.border}`,
              color: isUploading ? theme.palette.btnPrimary.disabled.color : theme.palette.btnPrimary.color,
              backgroundColor: isUploading ? theme.palette.btnPrimary.disabled.bg : theme.palette.btnPrimary.bg,
              fontSize: '13px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              userSelect: 'none',
            }}
          >
            <AttachFileIcon style={{ fontSize: '16px' }} />
            {isUploading ? 'Uploading…' : hasFile ? 'Replace file' : 'Upload file'}
          </label>

          <input
            id={fieldId}
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            disabled={isUploading}
            onChange={handleFileChange}
            aria-label={field.label}
            title={field.helpText}
          />

          {isUploading && (
            <span style={{ fontSize: '12px', color: '#888' }}>
              {state.progress > 0 && state.progress < 100 ? `${state.progress}%` : ''}
            </span>
          )}
        </div>

        {/* Success state */}
        {state.status === 'success' && displayFileName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: '#2e7d32',
            }}
          >
            <CheckCircleOutlineIcon style={{ fontSize: '16px' }} />
            <span>File uploaded successfully:&nbsp;</span>
            <span style={{ fontWeight: 500 }}>{displayFileName}</span>
          </div>
        )}

        {/* Pre-existing value (form loaded with an already-uploaded file) */}
        {state.status === 'idle' && typeof value === 'string' && value.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: '#555',
            }}
          >
            <DescriptionIcon style={{ fontSize: '16px', color: theme.palette.btnPrimary.color }} />
            <span>File already uploaded</span>
          </div>
        )}

        {/* Error state */}
        {state.status === 'error' && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#e53935',
            }}
          >
            {state.error ?? 'Upload failed. Please try again.'}
            <button
              type="button"
              onClick={reset}
              style={{
                background: 'none',
                border: 'none',
                color: theme.palette.btnPrimary.color,
                fontSize: '12px',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {errorMessage}
    </FormRowFullWidth>
  );
};

// Render file upload field
const renderFileField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean,
  fieldLabel: React.ReactNode,
  errorMessage: React.ReactNode,
  fieldError: string | undefined,
): React.ReactNode => {
  if (isReadOnly) {
    const identifier = typeof value === 'string' ? value : null;
    if (!identifier) {
      // Nothing was uploaded — show placeholder
      return (
        <FormRowFullWidth key={field.id}>
          {fieldLabel}
          <div style={{ padding: '8px 12px', color: '#888', fontSize: '13px' }}>No file uploaded</div>
          {errorMessage}
        </FormRowFullWidth>
      );
    }
    return (
      <FileFieldReadOnly
        key={field.id}
        identifier={identifier}
        fieldId={`dynamic-field-${field.id}`}
        fieldLabel={fieldLabel}
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <FileFieldUpload
      key={field.id}
      field={field}
      value={value}
      onChange={onChange}
      fieldLabel={fieldLabel}
      errorMessage={errorMessage}
      fieldError={fieldError}
    />
  );
};

export const renderDynamicField = (
  field: FormField,
  value: FormFieldValue,
  onChange: (fieldName: string, newValue: FormFieldValue) => void,
  isReadOnly: boolean = false,
  fieldError?: string,
  aiState?: AIFormState,
  _onAICorrection?: (fieldName: string, originalValue: unknown, correctedValue: unknown) => void,
  correctionsApplied?: boolean,
  animationPhase?: AnimationPhase,
  forceFullWidth: boolean = false,
): React.ReactNode => {
  const fieldId = `dynamic-field-${field.id}`;

  const fieldInsight = aiState?.patternInsights?.find(insight => insight.field === field.name);
  const hasAICorrection = fieldInsight?.autoCorrected === true;
  const hasCorrectionHistory = fieldInsight?.hasCorrectionHistory === true;

  const isAnimating = animationPhase === AI_ANIMATION.PHASES.ANIMATING;
  const shouldShowAsNeedsReview = hasCorrectionHistory && !correctionsApplied && !isAnimating;
  const shouldShowAsCorrected = hasAICorrection || (correctionsApplied && hasCorrectionHistory);

  const fieldLabel = createFieldLabel(field, fieldId, shouldShowAsCorrected, shouldShowAsNeedsReview, isAnimating);
  const styles = createFieldStyles(isReadOnly, fieldError, shouldShowAsNeedsReview);
  const errorMessage = createErrorMessage(fieldError);

  const aiCorrectionClassName = isAnimating ? 'ai-pulse-outline' : '';
  const needsCorrectionClassName = shouldShowAsNeedsReview ? 'needs-correction' : '';

  const FieldWrapper = forceFullWidth || field.type === 'textarea' ? FormRowFullWidth : FormColumn;

  switch (field.type) {
    case 'text':
      return renderTextField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, styles, FieldWrapper);

    case 'date':
      return renderDateField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, needsCorrectionClassName, fieldError, styles, FieldWrapper);

    case 'datetime':
      return renderDateTimeField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, styles, FieldWrapper);

    case 'select':
      return renderSelectField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, needsCorrectionClassName, styles, FieldWrapper);

    case 'multiselect':
      return renderMultiSelectField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, needsCorrectionClassName, styles, FieldWrapper);

    case 'radio':
      return renderRadioField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, needsCorrectionClassName, styles, FieldWrapper);

    case 'textarea':
      return renderTextAreaField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, aiCorrectionClassName, styles);

    case 'file':
      return renderFileField(field, value, onChange, isReadOnly, fieldLabel, errorMessage, fieldError);

    default:
      return null;
  }
};

